import React, {useEffect, useState} from 'react';
import {Switch, Text, TextInput, TouchableOpacity, View} from 'react-native';
import I18n from 'i18n-js';
import styles, {
  placeholderColor,
  switchThumbColor,
  switchTrackColor
} from '../../../style/adminTools';
import RuleEngineDropdown from '../../rulesEngine/RuleEngineDropdown';
import CompositeSettingRow from './CompositeSettingRow';
import {
  clockText,
  hourLabel,
  parseClock,
  rangeEndHour,
  rangeStartHour,
  withRangeHour,
  withTime
} from './isoTime';

const DIGITS = /^\d+$/;

const SubLabel = ({children}) => (
  <Text style={styles.settingSavingText}>{children}</Text>
);

const useText = initial => {
  const [text, setText] = useState(initial);
  useEffect(() => setText(initial), [initial]);
  return [text, setText];
};

const BarcodeControls = ({
  draft,
  blocked,
  save,
  idPrefix,
  stacked,
  setValidationError
}) => {
  const [length, setLength] = useText(String(draft.barcode_length ?? ''));
  const [start, setStart] = useText(String(draft.starting_value ?? ''));
  const changed =
    length !== String(draft.barcode_length ?? '') ||
    start !== String(draft.starting_value ?? '');

  const commit = () => {
    if (blocked || !changed) return;
    if (!DIGITS.test(length.trim()) || !DIGITS.test(start.trim())) {
      setValidationError(I18n.t('adminTools.invalidNumber'));
      return;
    }
    setValidationError(null);
    save({
      barcode_length: Number(length.trim()),
      starting_value: start.trim()
    });
  };

  const input = (name, text, setText, width, wide) => (
    <TextInput
      style={[
        styles.settingInputNumber,
        stacked
          ? {
              minWidth: wide ? 160 : 96,
              maxWidth: '100%',
              flexGrow: wide ? 1 : 0
            }
          : {width},
        stacked ? styles.settingInputPhone : null
      ]}
      value={text}
      onChangeText={next => {
        setText(next);
        setValidationError(null);
      }}
      onBlur={commit}
      onSubmitEditing={commit}
      editable={!blocked}
      keyboardType="numeric"
      placeholderTextColor={placeholderColor}
      accessibilityLabel={I18n.t(`tenantSettings.${name}`)}
      testID={`${idPrefix}-${name}`}
    />
  );

  return (
    <>
      <SubLabel>{I18n.t('tenantSettings.numericBarcodeLength')}</SubLabel>
      {input('numericBarcodeLength', length, setLength, 70)}
      <SubLabel>{I18n.t('tenantSettings.numericBarcodeStartValue')}</SubLabel>
      {input('numericBarcodeStartValue', start, setStart, 120, true)}
    </>
  );
};

const modeOptions = () => [
  {label: I18n.t('tenantSettings.importModeDaily'), value: 'Daily'},
  {label: I18n.t('tenantSettings.importModeHourly'), value: 'Hourly'}
];

const ScheduleControls = ({
  draft,
  blocked,
  save,
  idPrefix,
  stacked,
  setValidationError
}) => {
  const [time, setTime] = useText(clockText(draft.time_to_import_orders));
  const commitTime = () => {
    if (blocked || time === clockText(draft.time_to_import_orders)) return;
    const clock = parseClock(time);
    if (!clock) {
      setValidationError(I18n.t('tenantSettings.invalidTime'));
      return;
    }
    setValidationError(null);
    save({
      ...draft,
      time_to_import_orders: withTime(
        draft.time_to_import_orders,
        clock.hours,
        clock.minutes
      )
    });
  };

  return (
    <>
      <TextInput
        style={[
          styles.settingInputNumber,
          {width: 96},
          stacked ? styles.settingInputPhone : null
        ]}
        value={time}
        onChangeText={next => {
          setTime(next);
          setValidationError(null);
        }}
        onBlur={commitTime}
        onSubmitEditing={commitTime}
        editable={!blocked}
        placeholderTextColor={placeholderColor}
        accessibilityLabel={I18n.t('tenantSettings.importTime', {
          defaultValue: 'Import time'
        })}
        testID={`${idPrefix}-time`}
      />
      <View
        pointerEvents={blocked ? 'none' : 'auto'}
        style={blocked ? styles.settingInputLocked : null}
      >
        <RuleEngineDropdown
          options={modeOptions()}
          selectedValue={draft.schedule_import_mode}
          onSelect={next => {
            if (!blocked && next !== draft.schedule_import_mode)
              save({...draft, schedule_import_mode: next});
          }}
          testID={`${idPrefix}-mode`}
          triggerStyle={stacked ? styles.selectTriggerPhone : undefined}
        />
      </View>
      <TouchableOpacity
        style={[styles.switchHit, stacked ? styles.switchHitPhone : null]}
        onPress={() => {
          if (!blocked)
            save({
              ...draft,
              scheduled_order_import: !draft.scheduled_order_import
            });
        }}
        disabled={blocked}
        accessible={false}
        focusable={false}
        testID={`${idPrefix}-on-hit`}
      >
        <View pointerEvents="none">
          <Switch
            value={Boolean(draft.scheduled_order_import)}
            onValueChange={next =>
              !blocked && save({...draft, scheduled_order_import: next})
            }
            disabled={blocked}
            accessibilityLabel={I18n.t('tenantSettings.scheduleOnOff', {
              defaultValue: 'Run this schedule'
            })}
            trackColor={switchTrackColor}
            thumbColor={switchThumbColor}
            testID={`${idPrefix}-on`}
          />
        </View>
      </TouchableOpacity>
    </>
  );
};

const hours = (from, to) =>
  Array.from({length: to - from + 1}, (_, index) => {
    const value = from + index;
    return {label: hourLabel(value), value};
  });

const FtpControls = ({
  draft,
  blocked,
  save,
  idPrefix,
  stacked,
  setValidationError
}) => {
  const saveRange = next => {
    if (rangeStartHour(next.from_import) >= rangeEndHour(next.to_import)) {
      setValidationError(I18n.t('tenantSettings.rangeOrder'));
      return;
    }
    setValidationError(null);
    save(next);
  };
  return (
    <>
      <SubLabel>{I18n.t('tenantSettings.rangeFrom')}</SubLabel>
      <View
        pointerEvents={blocked ? 'none' : 'auto'}
        style={blocked ? styles.settingInputLocked : null}
      >
        <RuleEngineDropdown
          options={hours(0, 23)}
          selectedValue={rangeStartHour(draft.from_import)}
          onSelect={next => {
            if (!blocked && next !== rangeStartHour(draft.from_import))
              saveRange({
                ...draft,
                from_import: withRangeHour(draft.from_import, next)
              });
          }}
          testID={`${idPrefix}-from`}
          triggerStyle={stacked ? styles.selectTriggerPhone : undefined}
        />
      </View>
      <SubLabel>{I18n.t('tenantSettings.rangeTo')}</SubLabel>
      <View
        pointerEvents={blocked ? 'none' : 'auto'}
        style={blocked ? styles.settingInputLocked : null}
      >
        <RuleEngineDropdown
          options={hours(1, 24)}
          selectedValue={rangeEndHour(draft.to_import)}
          onSelect={next => {
            if (!blocked && next !== rangeEndHour(draft.to_import))
              saveRange({
                ...draft,
                to_import: withRangeHour(draft.to_import, next)
              });
          }}
          testID={`${idPrefix}-to`}
          triggerStyle={stacked ? styles.selectTriggerPhone : undefined}
        />
      </View>
    </>
  );
};

const WHOLE_DOLLARS = /^\d+$/;

const AddonControls = ({
  label,
  draft,
  blocked,
  save,
  idPrefix,
  stacked,
  setValidationError
}) => {
  const price = draft || {};
  const [amount, setAmount] = useText(String(price.amount ?? ''));
  const commitAmount = () => {
    if (blocked || amount === String(price.amount ?? '')) return;
    if (!WHOLE_DOLLARS.test(amount.trim())) {
      setValidationError(I18n.t('adminTools.invalidNumber'));
      return;
    }
    setValidationError(null);
    save({...price, amount: amount.trim()});
  };

  return (
    <>
      <TextInput
        style={[
          styles.settingInputNumber,
          {width: 96},
          stacked ? styles.settingInputPhone : null
        ]}
        value={amount}
        onChangeText={next => {
          setAmount(next);
          setValidationError(null);
        }}
        onBlur={commitAmount}
        onSubmitEditing={commitAmount}
        editable={!blocked}
        keyboardType="number-pad"
        placeholderTextColor={placeholderColor}
        accessibilityLabel={I18n.t('tenantSettings.addonPrice')}
        testID={`${idPrefix}-amount`}
      />
      <TouchableOpacity
        style={[styles.switchHit, stacked ? styles.switchHitPhone : null]}
        onPress={() => {
          if (!blocked) save({...price, toggle: !price.toggle});
        }}
        disabled={blocked}
        accessible={false}
        focusable={false}
        testID={`${idPrefix}-on-hit`}
      >
        <View pointerEvents="none">
          <Switch
            value={Boolean(price.toggle)}
            onValueChange={next => !blocked && save({...price, toggle: next})}
            disabled={blocked}
            accessibilityLabel={label}
            trackColor={switchTrackColor}
            thumbColor={switchThumbColor}
            testID={`${idPrefix}-on`}
          />
        </View>
      </TouchableOpacity>
    </>
  );
};

const CONTROLS = {
  addon: AddonControls,
  barcodes: BarcodeControls,
  schedule: ScheduleControls,
  ftpRange: FtpControls
};

const renderComposite = (definition, rowProps) => {
  const Controls = CONTROLS[definition.composite];
  if (!Controls) return null;
  return (
    <CompositeSettingRow
      key={definition.key}
      settingKey={definition.key}
      label={definition.label}
      tooltip={definition.tooltip}
      highlight={definition.highlight}
      disabled={definition.disabled}
      {...rowProps}
    >
      {api => <Controls {...api} />}
    </CompositeSettingRow>
  );
};

export default renderComposite;
